/**
 * WholesaleCustomersProvider
 * --------------------------
 * Session-scoped cache for `GET /api/v2/wholesale/customers` so that
 * navigating between wholesale routes does not refetch the full customer
 * list on every mount.  The V2 portal audit observed 375+ calls across
 * 14 routes; this provider caps the unfiltered list at one fetch per TTL
 * window (default 60s) per session.
 *
 * Scope
 * -----
 * This context is intentionally limited to the *unfiltered, active-only*
 * list (which is what sidebar-tabbed pages like /overview, /catalogue,
 * /orders, /customers, /exclusivity load on mount).  Search queries and
 * `include_inactive=true` bypass the cache -- those callers must still
 * hit the backend directly because the results cannot be reconstructed
 * from the cached slice.
 *
 * Types
 * -----
 * We keep the customer shape loose (`unknown` record) because the list
 * endpoint is consumed by components with different view projections
 * (CustomerListItem, CustomerSearch, AccountScorecardSection).  Each
 * consumer narrows with its own local interface.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useApi } from '../../../api';
import { useAuth } from '../../../AuthContext';

export interface WholesaleCustomerRow {
  id: number;
  customer_ref?: string | null;
  company_name?: string | null;
  business_name?: string | null;
  trading_name?: string | null;
  active?: boolean;
  order_count?: number | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  [key: string]: unknown;
}

interface WholesaleCustomersContextValue {
  customers: WholesaleCustomerRow[];
  loading: boolean;
  error: string | null;
  /** Force a refetch (e.g. after a create/update mutation). */
  refresh: () => Promise<void>;
  /** Timestamp of the last successful fetch, or null if never loaded. */
  lastFetchedAt: number | null;
}

const CACHE_TTL_MS = 60_000;

const WholesaleCustomersContext = createContext<WholesaleCustomersContextValue | null>(null);

interface ProviderProps {
  children: ReactNode;
}

export function WholesaleCustomersProvider({ children }: ProviderProps) {
  const { token, logout } = useAuth();
  const { apiFetch } = useApi(token, logout);

  const [customers, setCustomers] = useState<WholesaleCustomerRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);

  // Dedupe: if a fetch is already inflight when a consumer mounts, everyone
  // awaits the same promise instead of starting N concurrent requests.
  const inflightRef = useRef<Promise<void> | null>(null);

  const doFetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v2/wholesale/customers');
      const data = await res.json() as { data?: WholesaleCustomerRow[] } | WholesaleCustomerRow[];
      const list: WholesaleCustomerRow[] = Array.isArray(data)
        ? data
        : (data.data || []);
      setCustomers(list);
      setLastFetchedAt(Date.now());
    } catch (err) {
      setError((err as Error).message || 'Failed to load customers');
    } finally {
      setLoading(false);
      inflightRef.current = null;
    }
  }, [apiFetch]);

  const refresh = useCallback(async (): Promise<void> => {
    if (inflightRef.current) {
      return inflightRef.current;
    }
    const p = doFetch();
    inflightRef.current = p;
    return p;
  }, [doFetch]);

  // Initial load when the token becomes available. TTL is checked via ref so
  // identity changes of doFetch/apiFetch do not re-trigger storms on SPA nav
  // (audit: 375+ /api/v2/wholesale/customers calls across route changes).
  const lastFetchedAtRef = useRef<number | null>(null);
  useEffect(() => {
    lastFetchedAtRef.current = lastFetchedAt;
  }, [lastFetchedAt]);

  useEffect(() => {
    if (!token) return;
    const last = lastFetchedAtRef.current;
    if (last !== null && Date.now() - last < CACHE_TTL_MS) {
      return;
    }
    if (inflightRef.current) return;
    const p = doFetch();
    inflightRef.current = p;
  }, [token, doFetch]);

  const value = useMemo<WholesaleCustomersContextValue>(
    () => ({ customers, loading, error, refresh, lastFetchedAt }),
    [customers, loading, error, refresh, lastFetchedAt],
  );

  return (
    <WholesaleCustomersContext.Provider value={value}>
      {children}
    </WholesaleCustomersContext.Provider>
  );
}

/**
 * Access the shared wholesale customers list.  Returns `null` if used
 * outside the provider (so legacy callers can fall back to their own
 * fetcher without throwing during a migration).
 */
export function useWholesaleCustomers(): WholesaleCustomersContextValue | null {
  return useContext(WholesaleCustomersContext);
}
